/**
 * Service Worker registration and management utilities
 */

import React from 'react';
import { jsonAuthHeaders } from './authHeaders';
import { runtimeConfig } from '../config/runtime';

const apiUrl = (path: string): string => `${runtimeConfig.apiUrl}${path}`;

// Check if service workers are supported
const isSupported = (): boolean => {
  return 'serviceWorker' in navigator;
};

// Opt-out switch — set localStorage["disable-sw"] = "1" to skip registration.
// We keep the SW enabled in dev too because push notifications and the install
// prompt rely on it; without it, the notification banner is a dead-end.
const isExplicitlyDisabled = (): boolean => {
  try {
    return localStorage.getItem('disable-sw') === '1';
  } catch {
    return false;
  }
};

// Service worker registration options
interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onOfflineReady?: () => void;
  onNeedRefresh?: () => void;
}

// Register service worker
export const registerServiceWorker = async (config: ServiceWorkerConfig = {}): Promise<ServiceWorkerRegistration | null> => {
  if (!isSupported()) {
    console.warn('Service workers are not supported in this browser');
    return null;
  }

  if (isExplicitlyDisabled()) {
    console.log('Service worker registration skipped (disable-sw flag set)');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });

    console.log('Service worker registered successfully:', registration);

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New content is available
            console.log('New content is available; please refresh.');
            config.onUpdate?.(registration);
            config.onNeedRefresh?.();
          } else {
            // Content is cached for offline use
            console.log('Content is cached for offline use.');
            config.onSuccess?.(registration);
            config.onOfflineReady?.();
          }
        }
      });
    });

    // Listen for controlling service worker changes
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('Service worker controller changed');
      window.location.reload();
    });

    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return null;
  }
};

// Unregister service worker
export const unregisterServiceWorker = async (): Promise<boolean> => {
  if (!isSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const result = await registration.unregister();
    console.log('Service worker unregistered:', result);
    return result;
  } catch (error) {
    console.error('Service worker unregistration failed:', error);
    return false;
  }
};

// Update service worker
export const updateServiceWorker = async (): Promise<void> => {
  if (!isSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.update();
    console.log('Service worker update check completed');
  } catch (error) {
    console.error('Service worker update failed:', error);
  }
};

// Skip waiting and activate new service worker
export const skipWaiting = (): void => {
  if (!isSupported() || !navigator.serviceWorker.controller) {
    return;
  }

  navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
};

// Get service worker version
export const getServiceWorkerVersion = async (): Promise<string | null> => {
  if (!isSupported() || !navigator.serviceWorker.controller) {
    return null;
  }

  return new Promise((resolve) => {
    const messageChannel = new MessageChannel();
    messageChannel.port1.onmessage = (event) => {
      resolve(event.data.version || null);
    };

    navigator.serviceWorker.controller.postMessage(
      { type: 'GET_VERSION' },
      [messageChannel.port2]
    );

    // Timeout after 5 seconds
    setTimeout(() => resolve(null), 5000);
  });
};

// Check if app is running offline
export const isOffline = (): boolean => {
  return !navigator.onLine;
};

// Listen for online/offline events
export const addNetworkListeners = (
  onOnline?: () => void,
  onOffline?: () => void
): (() => void) => {
  const handleOnline = () => {
    console.log('App is online');
    onOnline?.();
  };

  const handleOffline = () => {
    console.log('App is offline');
    onOffline?.();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

// Cache management utilities
export const clearCache = async (cacheName?: string): Promise<boolean> => {
  if (!('caches' in window)) {
    return false;
  }

  try {
    if (cacheName) {
      return await caches.delete(cacheName);
    } else {
      const cacheNames = await caches.keys();
      const deletePromises = cacheNames.map(name => caches.delete(name));
      await Promise.all(deletePromises);
      return true;
    }
  } catch (error) {
    console.error('Failed to clear cache:', error);
    return false;
  }
};

// Get cache storage estimate
export const getCacheStorageEstimate = async (): Promise<StorageEstimate | null> => {
  if (!('storage' in navigator) || !navigator.storage.estimate) {
    return null;
  }

  try {
    return await navigator.storage.estimate();
  } catch (error) {
    console.error('Failed to get storage estimate:', error);
    return null;
  }
};

// Background sync utilities
export const requestBackgroundSync = async (tag: string): Promise<void> => {
  if (!isSupported()) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    if ('sync' in registration) {
      await (registration as any).sync.register(tag);
      console.log('Background sync registered:', tag);
    }
  } catch (error) {
    console.error('Background sync registration failed:', error);
  }
};

// Push notification utilities
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  console.log('Notification permission:', permission);
  return permission;
};

// VAPID keys are URL-safe base64; pushManager.subscribe expects a BufferSource.
// Return ArrayBuffer (not Uint8Array<ArrayBufferLike>) so TS accepts it as BufferSource.
const urlBase64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i += 1) view[i] = raw.charCodeAt(i);
  return buffer;
};

const fetchVapidPublicKey = async (): Promise<string | null> => {
  try {
    const res = await fetch(apiUrl('/api/push/vapid-public-key'), { credentials: 'include' });
    if (!res.ok) return null;
    const json = await res.json();
    return json?.data?.publicKey || null;
  } catch {
    return null;
  }
};

// Subscribe to push notifications.
// The VAPID public key is fetched at runtime (not baked in at build time) so that
// rotating keys doesn't require rebuilding the frontend bundle.
export type SubscribeFailureReason =
  | 'unsupported'
  | 'permission'
  | 'vapid'
  | 'browser-subscribe'
  | 'backend-register'
  | 'no-subscription';

export type SubscribeResult =
  | { ok: true; subscription: PushSubscription }
  | { ok: false; reason: SubscribeFailureReason; detail?: string };

export interface SubscribeOptions {
  // When true (Enable button), create a fresh subscription if the browser doesn't
  // already have one. When false (mount-time auto-sync), only re-register the
  // existing subscription with the backend — don't silently re-create after the
  // user explicitly disabled the device.
  createIfMissing: boolean;
}

// Send the current JWT and API base URL to the active SW so it can re-authenticate
// and reach the backend when handling pushsubscriptionchange (which fires without
// page context — no localStorage and no runtimeConfig).
const cacheAuthInServiceWorker = (): void => {
  try {
    const token = localStorage.getItem('auth_token');
    navigator.serviceWorker.controller?.postMessage({
      type: 'CACHE_PUSH_CONFIG',
      token: token || null,
      apiUrl: runtimeConfig.apiUrl,
    });
  } catch {
    // localStorage blocked or no controller — ignore.
  }
};

export const subscribeToPushNotifications = async (
  options: SubscribeOptions = { createIfMissing: true }
): Promise<SubscribeResult> => {
  if (!isSupported()) {
    return { ok: false, reason: 'unsupported', detail: 'service worker not supported' };
  }

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch (error) {
    return { ok: false, reason: 'unsupported', detail: errorDetail(error) };
  }

  if (!('pushManager' in registration)) {
    return { ok: false, reason: 'unsupported', detail: 'PushManager not on registration' };
  }

  const existing = await registration.pushManager.getSubscription();

  if (!existing && !options.createIfMissing) {
    // Auto-sync path: device was disabled (or never enabled). Don't recreate
    // silently — the Enable button is the only place that should mint a new sub.
    return { ok: false, reason: 'no-subscription', detail: 'no existing subscription to sync' };
  }

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: 'permission', detail: `Notification.permission=${permission}` };
  }

  let subscription: PushSubscription;
  if (existing) {
    subscription = existing;
  } else {
    const vapidPublicKey = await fetchVapidPublicKey();
    if (!vapidPublicKey) {
      return { ok: false, reason: 'vapid', detail: '/api/push/vapid-public-key returned no key' };
    }

    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(vapidPublicKey),
      });
    } catch (error) {
      // The most common reason is InvalidStateError: a subscription already exists for a
      // different applicationServerKey (e.g. VAPID key was rotated, or browser cache is stale).
      // Standard recovery is to unsubscribe the existing one and try again once.
      if (errorName(error) === 'InvalidStateError') {
        try {
          const stale = await registration.pushManager.getSubscription();
          if (stale) await stale.unsubscribe();
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToArrayBuffer(vapidPublicKey),
          });
        } catch (retryError) {
          return {
            ok: false,
            reason: 'browser-subscribe',
            detail: `${errorName(retryError)}: ${errorDetail(retryError)} (after InvalidStateError recovery)`,
          };
        }
      } else {
        return {
          ok: false,
          reason: 'browser-subscribe',
          detail: `${errorName(error)}: ${errorDetail(error)}`,
        };
      }
    }
  }

  const backendResult = await registerSubscriptionWithBackend(subscription);
  if (!backendResult.ok) {
    return backendResult;
  }

  cacheAuthInServiceWorker();
  return { ok: true, subscription };
};

const errorName = (error: unknown): string =>
  (error instanceof Error && error.name) || 'Error';

const errorDetail = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  try {
    return String(error);
  } catch {
    return 'unknown error';
  }
};

type BackendResult =
  | { ok: true }
  | { ok: false; reason: 'backend-register'; detail: string };

const registerSubscriptionWithBackend = async (sub: PushSubscription): Promise<BackendResult> => {
  const payload = sub.toJSON();
  let response: Response;
  try {
    response = await fetch(apiUrl('/api/push/subscribe'), {
      method: 'POST',
      headers: jsonAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        endpoint: payload.endpoint,
        keys: payload.keys,
      }),
    });
  } catch (error) {
    return { ok: false, reason: 'backend-register', detail: `network: ${errorDetail(error)}` };
  }

  if (!response.ok) {
    let body = '';
    try {
      body = (await response.text()).slice(0, 200);
    } catch {
      /* ignore */
    }
    return {
      ok: false,
      reason: 'backend-register',
      detail: `HTTP ${response.status}${body ? ` — ${body}` : ''}`,
    };
  }

  return { ok: true };
};

export const unsubscribeFromPushNotifications = async (): Promise<boolean> => {
  if (!isSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();
    if (!sub) return true;

    await fetch(apiUrl('/api/push/subscribe'), {
      method: 'DELETE',
      headers: jsonAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ endpoint: sub.endpoint }),
    }).catch(() => undefined);

    return sub.unsubscribe();
  } catch {
    return false;
  }
};

// React hook for service worker management
export const useServiceWorker = (config: ServiceWorkerConfig = {}) => {
  const [isOnline, setIsOnline] = React.useState(!isOffline());
  const [needsRefresh, setNeedsRefresh] = React.useState(false);
  const [offlineReady, setOfflineReady] = React.useState(false);

  React.useEffect(() => {
    const cleanup = addNetworkListeners(
      () => setIsOnline(true),
      () => setIsOnline(false)
    );

    registerServiceWorker({
      ...config,
      onNeedRefresh: () => {
        setNeedsRefresh(true);
        config.onNeedRefresh?.();
      },
      onOfflineReady: () => {
        setOfflineReady(true);
        config.onOfflineReady?.();
      }
    });

    return cleanup;
  }, []);

  return {
    isOnline,
    needsRefresh,
    offlineReady,
    updateServiceWorker: skipWaiting,
    clearCache,
    getCacheStorageEstimate
  };
};
